import * as React from "react";
import { FileText, Edit, Trash2, Zap } from "lucide-react";
import { Button } from "../ui/button";
import { Request } from "@/types/research.types";
import { marked } from "marked";
import DOMPurify from "dompurify";

interface RequestItemProps {
    request: Request;
    onClick: (content: string, request: Request) => void;
    onEdit: (e: React.MouseEvent, request: Request) => void;
    onDelete: (e: React.MouseEvent, requestId: string) => void;
}

const RequestItem: React.FC<RequestItemProps> = ({ request, onClick, onEdit, onDelete }) => {
    const renderMarkdown = (text: string) => {
        const renderer = new marked.Renderer();
        // Override block elements to be inline
        renderer.heading = ({ text }) => `${marked.parseInline(text)} `;
        renderer.paragraph = ({ text }) => `${marked.parseInline(text)} `;
        renderer.list = ({ items }) => {
            return items.map((item) => `• ${marked.parseInline(item.text)} `).join("");
        };
        renderer.br = () => " ";
        renderer.blockquote = ({ text }) => `${marked.parseInline(text)} `;
        renderer.strong = ({ text }) => text;
        renderer.em = ({ text }) => text;
        renderer.codespan = ({ text }) => text;

        const html = marked.parse(text, { renderer, async: false }) as string;
        const sanitizedHtml = DOMPurify.sanitize(html);
        const strippedHtml = sanitizedHtml.replace(/[\r\n]+/g, " ");
        return { __html: strippedHtml };
    };

    return (
        <div
            className="flex items-center justify-between px-3 py-2 bg-primary-100 rounded-lg transition-all cursor-pointer"
            onClick={() => onClick(request.content, request)}
        >
            <div className="flex items-center gap-2 flex-1 min-w-0">
                {request.chain_with && request.chain_with.length > 0 ? (
                    <Zap className="size-3.5 text-warning-200 shrink-0" />
                ) : (
                    <FileText className="size-3.5 text-neutral-400 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                    <div
                        className="text-sm font-medium text-neutral-400 line-clamp-1"
                        dangerouslySetInnerHTML={renderMarkdown(request.name)}
                    />
                    <div
                        className="text-xs text-neutral-500 line-clamp-1"
                        dangerouslySetInnerHTML={renderMarkdown(request.content)}
                    />
                </div>
            </div>
            <div className="flex items-center gap-3 ml-2 shrink-0">
                <Button
                    variant="ghost"
                    className="w-auto h-auto p-0"
                    size="icon"
                    onClick={(e) => onEdit(e, request)}
                >
                    <Edit className="size-3.5" />
                </Button>
                <Button
                    variant="ghost"
                    className="w-auto h-auto p-0"
                    size="icon"
                    onClick={(e) => onDelete(e, request.id)}
                >
                    <Trash2 className="size-3.5 text-danger-200" />
                </Button>
            </div>
        </div>
    );
};

export default RequestItem;
